<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
// Passo 1, "Por onde sai". Segue a convenção deste diretório: texto em pt-BR
// direto, sem chave de tradução (o formulário de campanha é operado só pela
// equipe brasileira e o texto muda junto com a operação).
//
// Decisão 30: canal sem nenhuma caixa utilizável aparece indisponível, com o
// motivo escrito, e não dá para escolher. O nome da campanha saiu daqui para a
// tela de conferir, porque quem entra pelo cartão "Nova ação de marketing" não
// vê este passo.
import { computed } from 'vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  state: { type: Object, required: true },
  inboxOptions: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  // { waha: { available, reason }, official: {...}, sms: {...} }
  availability: { type: Object, default: () => ({}) },
  // Quando a pessoa chegou por um canal já escolhido fora, os outros somem.
  allowedChannels: { type: Array, default: null },
});

const emit = defineEmits(['changeChannel']);

const ALL_CHANNELS = [
  {
    value: 'waha',
    icon: 'i-ph-qr-code',
    label: 'WhatsApp por QR code',
    hint: 'Número conectado pelo celular. Texto livre, sem aprovação. Não informa entrega nem leitura.',
  },
  {
    value: 'official',
    icon: 'i-ph-shield-check',
    label: 'WhatsApp oficial',
    hint: 'API da Meta. Só modelo aprovado. Informa entrega e leitura.',
  },
  {
    value: 'sms',
    icon: 'i-ph-chat-dots',
    label: 'SMS',
    hint: 'Texto curto, sem mídia.',
  },
];

const channels = computed(() => {
  const allowed = props.allowedChannels;
  const list = allowed?.length
    ? ALL_CHANNELS.filter(channel => allowed.includes(channel.value))
    : ALL_CHANNELS;
  return list.map(channel => ({
    ...channel,
    available: props.availability[channel.value]?.available !== false,
    reason: props.availability[channel.value]?.reason || '',
  }));
});

const hasInboxes = computed(() => props.inboxOptions.length > 0);

const currentReason = computed(
  () => props.availability[props.state.channelType]?.reason || ''
);

const pick = channel => {
  if (!channel.available) return;
  emit('changeChannel', channel.value);
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      class="grid grid-cols-1 gap-2"
      :class="channels.length > 1 ? 'sm:grid-cols-3' : ''"
    >
      <button
        v-for="channel in channels"
        :key="channel.value"
        type="button"
        class="flex flex-col gap-1 p-3 text-left border rounded-xl transition-colors"
        :class="[
          !channel.available
            ? 'border-n-weak opacity-60 cursor-not-allowed'
            : state.channelType === channel.value
              ? 'border-n-blue-8 bg-n-alpha-black2'
              : 'border-n-weak hover:bg-n-alpha-black2',
        ]"
        :disabled="!channel.available"
        :aria-pressed="state.channelType === channel.value"
        :data-channel="channel.value"
        :data-available="channel.available ? 'yes' : 'no'"
        @click="pick(channel)"
      >
        <span class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
          <span :class="channel.icon" class="size-4" />
          {{ channel.label }}
        </span>
        <span v-if="!channel.available" class="text-xs text-n-amber-11">
          Indisponível: {{ channel.reason }}
        </span>
        <span v-else class="text-xs text-n-slate-11">{{ channel.hint }}</span>
      </button>
    </div>

    <div class="flex flex-col gap-1">
      <label for="campaign-inbox" class="text-sm font-medium text-n-slate-12">
        Caixa
      </label>
      <p v-if="loading" class="text-xs text-n-slate-11">Carregando caixas...</p>
      <ComboBox
        v-else-if="hasInboxes"
        id="campaign-inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        placeholder="Escolha a caixa que vai enviar"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <p v-else class="text-xs text-n-amber-11">
        {{
          currentReason
            ? `Indisponível: ${currentReason}.`
            : 'Nenhuma caixa deste tipo está conectada nesta conta.'
        }}
        Conecte ou reconecte uma caixa em Configurações para usar este canal.
      </p>
      <p v-if="hasInboxes" class="text-xs text-n-slate-11">
        Caixa desconectada não entra nesta lista.
      </p>
    </div>
  </div>
</template>
